/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.hideOptions(this.variant);
      this.hideOptionsWithNoSKU();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();
      // Safari doesn't support optional chaining, so use traditional checks
      const optionType = evt && evt.target && evt.target.dataset && evt.target.dataset.optionType ? evt.target.dataset.optionType : null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
        this.hideOptionsWithNoSKU();
        this.hideOptions(this.variant);
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.hideOptionsWithNoSKU();
      // If the event is from the first selector, update the selected second option variant to the first available option
      if ((optionType === '產品類型' || optionType === 'Product Type') && optionType !== null) {
        this.updateVariantWithFirstAvailableOption();
      }
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    /**
     * Update the variant with the first available option
     */
    updateVariantWithFirstAvailableOption() {
      const { selectorType } = this.dataset; // 'downward' or 'selection'
      const options =
        selectorType === "listed"
          ? this.optionSelectors[1].querySelectorAll(
              '.js-option:not([data-value=""])'
            )
          : this.optionSelectors[1].querySelectorAll(
              '.custom-select__option[role="option"]'
            );
      for (const option of options) {
        if (!option.hidden && !option.classList.contains('is-unavailable')) {
          if (selectorType === "listed") {
            option.checked = true;
          } else {
            // For dropdown type, simulate selection by updating button text
            const selectBtn = this.optionSelectors[1].querySelector(
              ".custom-select__btn"
            );
            if (selectBtn) {
              selectBtn.textContent = option.textContent.trim();
            }
          }
          break;
        }
      }

      // Run the handleVariantChange method to update the variant
      this.handleVariantChange();
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();

      if (!this.variant) {
        currVariant = { options: selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      if (!evt) return;
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant;
      this.price.querySelector('.price__no-variant').hidden = this.variant;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }

    /**
     * Create a set of options based on the option1 values
     * {
     *   option1: {
     *    option2: available
     *    option2: available
     *   },
     *   option1: {
     *    option2: available
     *    option2: available
     *   },
     * }
     * @returns {?object}
     * return {
     *   "寢具套裝": {
     *    "3尺單人": true
     *    "4尺單人": true
     *   },
     *   "床品套裝": {
     *    "3尺單人": true
     *    "4尺單人": true
     *   },
     * }
     */
    createOptionSet() {
      const variants = this.data.product.variants;
      const optionsSet = {};
      variants.forEach((v) => {
        if (optionsSet.hasOwnProperty(v.option1)) {
          optionsSet[v.option1][v.option2] = v.available;
        } else {
          optionsSet[v.option1] = { [v.option2]: v.available };
        }
      });

      return optionsSet;
    }

    /**
     * Special requirement
     * When GBS, aka "床品套裝" is available, hide "寢具套裝" options, aka eGBS
     * When GBS is unavailable, hide "床品套裝" options and show "寢具套裝" options instead
     */
    hideOptions(selectedVariant) {
      const { selectorType } = this.dataset; // 'downward' or 'selection'
      const GBS = "床品套裝";
      const GBS_EN = "Bedding Set";
      const eGBS = "寢具套裝";
      const eGBS_EN = "Bedding Kit";

      // Create a set of options based on the option1 values
      const optionsSet = this.createOptionSet();
      const inventoryQuantity = this.data.inventory_quantity;

      const GBSInventoryQuantity = inventoryQuantity
        .filter(v => v.option1 === GBS || v.option1 === GBS_EN)
        .reduce((sum, v) => sum + v.inventory_quantity, 0);
      const eGBSInventoryQuantity = inventoryQuantity
        .filter(v => v.option1 === eGBS || v.option1 === eGBS_EN)
        .reduce((sum, v) => sum + v.inventory_quantity, 0);

      const iseGBSExisted =
        optionsSet.hasOwnProperty(eGBS) || optionsSet.hasOwnProperty(eGBS_EN);
      if (!iseGBSExisted) return;

      if (
        !optionsSet.hasOwnProperty(GBS) &&
        !optionsSet.hasOwnProperty(GBS_EN) &&
        !optionsSet.hasOwnProperty(eGBS) &&
        !optionsSet.hasOwnProperty(eGBS_EN)
      )
        return;

      // Check if the key are all false in its value, if so, the option is not available
      // Return true if option has at least one true value
      const checkOptionsAvailable = (key) => {
        if (!optionsSet.hasOwnProperty(key)) return false;
        return !Object.values(optionsSet[key]).every(value => value === false)
      };
      const isGBSAvailable =
        checkOptionsAvailable(GBS) || checkOptionsAvailable(GBS_EN);
      // const iseGBSAvailable =
      //   checkOptionsAvailable(eGBS) || checkOptionsAvailable(eGBS_EN);

      const checkAllObjectsHaveTrueValue = (obj, exceptKeys) => {
        const result = Object.entries(obj).reduce((acc, [key, value]) => {
          if (!exceptKeys.includes(key)) {
            if (typeof value === 'object' && value !== null) {
              acc[key] = Object.values(value).some(v => v === true);
            } else {
              acc[key] = false; // If the value is not an object, consider it as false
            }
          }
          return acc;
        }, {});
      
        // Check if all values in the result object are true
        return Object.values(result).every(value => value === true);
      }

      // Check if any other key (excluding GBS and eGBS) is available
      // If no false value is found, then all other options are available
      const isOtherAvailable = checkAllObjectsHaveTrueValue(
        optionsSet,
        [GBS, eGBS, GBS_EN, eGBS_EN]
      );

      // Get all the option selectors and hide/show the options based on the availability
      this.optionSelectors.forEach((selector) => {
        const options =
          selectorType === "listed"
            ? selector.querySelectorAll('.js-option:not([data-value=""])')
            : selector.querySelectorAll(
                '.custom-select__option[role="option"]'
              );

        options.forEach((option) => {
          if (selectorType === "listed") {
            const isGBSOption =
              option.nextElementSibling.innerText.trim() === GBS ||
              option.nextElementSibling.innerText.trim() === GBS_EN;
            const isEGBSOption =
              option.nextElementSibling.innerText.trim() === eGBS ||
              option.nextElementSibling.innerText.trim() === eGBS_EN;

            if (eGBSInventoryQuantity > GBSInventoryQuantity && isGBSOption) {
              // If eGBS inventory quantity is greater than GBS inventory quantity and the option is GBS, hide the option
              option.hidden = true;
              option.nextElementSibling.hidden = true;
            } else if (eGBSInventoryQuantity > GBSInventoryQuantity && isEGBSOption) {
              // If eGBS inventory quantity is greater than GBS inventory quantity and the option is eGBS, show the option
              option.hidden = false;
              option.nextElementSibling.hidden = false;
            } else if (isGBSAvailable && isGBSOption) {
              // If GBS is available and the option is GBS, show the option
              option.hidden = false;
              option.nextElementSibling.hidden = false;
            } else if (isGBSAvailable && isEGBSOption) {
              // If GBS is available and the option is eGBS, hide the option
              option.hidden = true;
              option.nextElementSibling.hidden = true;
            } else if (!isGBSAvailable && !isOtherAvailable && isGBSOption) {
              // If GBS is unavailable, other options has one unavailable, and the option is GBS, show the option
              option.hidden = false;
              option.nextElementSibling.hidden = false;
            } else if (!isGBSAvailable && !isOtherAvailable && isEGBSOption) {
              // If GBS is unavailable, other options has one unavailable, and the option is eGBS, hide the option
              option.hidden = true;
              option.nextElementSibling.hidden = true;
            } else if (!isGBSAvailable && isGBSOption) {
              // If GBS is unavailable and the option is GBS, hide the option
              option.hidden = true;
              option.nextElementSibling.hidden = true;
            } else if (!isGBSAvailable && isEGBSOption) {
              // If GBS is unavailable and the option is eGBS, show the option
              option.hidden = false;
              option.nextElementSibling.hidden = false;
            } else {
              // If the option is neither GBS nor eGBS, show the option
              option.hidden = false;
              option.nextElementSibling.hidden = false;
            }
          } else {
          const isGBSOption =
            option.textContent.trim().includes(GBS) ||
            option.textContent.trim().includes(GBS_EN);
          const isEGBSOption =
            option.textContent.trim().includes(eGBS) ||
            option.textContent.trim().includes(eGBS_EN);

            if (eGBSInventoryQuantity > GBSInventoryQuantity && isGBSOption) {
              option.hidden = true;
            } else if (eGBSInventoryQuantity > GBSInventoryQuantity && isEGBSOption) {
              option.hidden = false;
            } else if (isGBSAvailable && isGBSOption) {
              option.hidden = false;
            } else if (isGBSAvailable && isEGBSOption) {
              option.hidden = true;
            } else if (!isGBSAvailable && !isOtherAvailable && isGBSOption) {
              option.hidden = false;
            } else if (!isGBSAvailable && !isOtherAvailable && isEGBSOption) {
              option.hidden = true;
            } else if (!isGBSAvailable && isGBSOption) {
              option.hidden = true;
            } else if (!isGBSAvailable && isEGBSOption) {
              option.hidden = false;
            } else {
              option.hidden = false;
            }
          }
        });
      });
    }

    /**
     * Special requirement
     * Hide second options with no SKU for selected first option
     */
    hideOptionsWithNoSKU() {
      // If all variants have no option2 and option3, no need to hide options
      if (this.data.product.variants.every(v => v.option2 === null && v.option3 === null)) {
        return;
      }
      const { selectorType } = this.dataset; // 'downward' or 'selection'
      const optionsSet = this.createOptionSet();
      // Get the selected first and second options - ["寢具套裝", "3尺單人"]
      const selectedOptions = this.getSelectedOptions();
      // Get the selected first option - "寢具套裝"
      const selectedFirstOption = selectedOptions[0];
      // Get the second option availability values of the selected first option - { "3尺單人": true, "4尺單人": true }
      const selectedFirstOptionVariantValues = optionsSet[selectedFirstOption];

      const optionSelector = this.optionSelectors.length > 1 ? this.optionSelectors[1] : this.optionSelectors[0];
      const options =
        selectorType === "listed"
          ? optionSelector.querySelectorAll(
              '.js-option:not([data-value=""])'
            )
          : optionSelector.querySelectorAll(
              '.custom-select__option[role="option"]'
            );
      options.forEach((option) => {
        const optionValue =
          selectorType === "listed" ? option.value : option.textContent.trim();

        // If the second option is not in the selected first option object, meaning the second option has no SKU, hide the option
        if (
          !selectedFirstOptionVariantValues ||
          !selectedFirstOptionVariantValues.hasOwnProperty(optionValue)
        ) {
          option.hidden = true;
          if (option.nextElementSibling) {
            option.nextElementSibling.hidden = true;
          }
        } else {
          option.hidden = false;
          if (option.nextElementSibling) {
            option.nextElementSibling.hidden = false;
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
